import React, { SyntheticEvent } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { MatchStrength, Person } from 'talent_matching/types';
import { withLocalePrefix } from 'shared/utils/translation';
import StrengthIndicator from 'talent_matching/components/shared/strength_indicator';
import styled from 'styled-components';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import AiMagicIcon from '@grnhse/seedling/lib/birch/icons/ai_magic';
import { useRankingInProgress } from 'talent_matching/contexts/ranking_in_progress_context';
import MatchingSkills from 'talent_matching/components/table/matching_skills';
import { WarningTag } from '@grnhse/seedling/lib/birch/components/tags';

const t = withLocalePrefix('talent_matching');

type Props = {
  candidate: Person;
  openCalibrationModal: () => void;
};

const ButtonWrapper = styled.div`
  button {
    padding-left: 0;
  }
`;

const MatchStrengthCell = ({ candidate, openCalibrationModal }: Props) => {
  const { hasCalibrationSetting } = useRankingInProgress();

  const hasScores =
    candidate.matchScore !== null && candidate.matchStrength !== null;

  const parsingInProgress = candidate.resume?.parsing_status === 'in_progress';
  const parsingFailed = candidate.resume?.parsing_status === 'failed';

  const handleClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    openCalibrationModal();
  };

  const isOverride = !!candidate?.overrideMatchStrength;

  const matchStrength = isOverride
    ? candidate?.overrideMatchStrength
    : candidate?.matchStrength;

  if (!hasCalibrationSetting) {
    return (
      <Stack
        sx={{
          padding: 1,
          paddingLeft: 0,
          height: '100%',
        }}
        spacing={0}
        alignContent="flex-start"
      >
        <SecondaryBody metadata>
          {t('match_strength_explanation.set_calibration')}
        </SecondaryBody>
        <ButtonWrapper>
          <TertiaryButton onClick={handleClick} size="md" icon={AiMagicIcon}>
            {t('match_strength_explanation.calibrate')}
          </TertiaryButton>
        </ButtonWrapper>
      </Stack>
    );
  }

  if (!hasScores || parsingInProgress || parsingFailed) {
    const statusMessage = parsingInProgress
      ? t('resume_details.parsing_in_progress')
      : t('match_strength_explanation.needs_manual_review');

    return (
      <Stack
        sx={{
          padding: 1,
          paddingLeft: 0,
          height: '100%',
        }}
        alignContent="flex-start"
      >
        <SecondaryBody metadata>{statusMessage}</SecondaryBody>
      </Stack>
    );
  }

  return (
    <Stack
      spacing={1}
      sx={{
        padding: 1,
        paddingLeft: 0,
        paddingTop: 1.5,
        height: '100%',
      }}
    >
      <StrengthIndicator matchStrength={matchStrength as MatchStrength} />
      <Stack direction="row" spacing={1} alignItems="center">
        <SecondaryBody>{t(`match_strengths.${matchStrength}`)}</SecondaryBody>
        {isOverride && <WarningTag text={t('override')} />}
      </Stack>
      <MatchingSkills
        skills={candidate.matchedSkills || []}
        openCalibrationModal={openCalibrationModal}
      />
    </Stack>
  );
};

export default MatchStrengthCell;
